import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ChevronRight, Phone, Video } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { AI_BOOKINGS, CalendarEvent } from '../data/calendarEvents';
interface BookingsScreenProps {
  onBack: () => void;
  onOpenInCalendar: (eventId: number) => void;
}
const TODAY_DAY = 24;

/** Summary of meetings Vexu booked from calls, grouped by date. */
export function BookingsScreen({
  onBack,
  onOpenInCalendar
}: BookingsScreenProps) {
  const {
    isLight
  } = useTheme();
  const groups = AI_BOOKINGS.reduce<{
    date: string;
    day: number;
    items: CalendarEvent[];
  }[]>((acc, e) => {
    const g = acc.find(x => x.day === e.day);
    if (g) g.items.push(e);else acc.push({
      date: e.date,
      day: e.day,
      items: [e]
    });
    return acc;
  }, []);
  const dayLabel = (g: {
    date: string;
    day: number;
  }) => {
    const short = g.date.replace(/^(\w{3})\w*, (\w{3})\w* (\d+)$/, '$1, $2 $3');
    if (g.day === TODAY_DAY) return `Today · ${short}`;
    if (g.day === TODAY_DAY + 1) return `Tomorrow · ${short}`;
    return short;
  };
  return <div className={`flex h-full flex-col ${isLight ? 'bg-slate-50' : 'bg-[#0A0A0A]'}`} data-id="element-1085" data-mp="3437izima1b1l">
      <header className="px-5 pb-4 pt-6" data-id="element-1086" data-mp="38upn2fjljrqs">
        <button type="button" onClick={onBack} aria-label="Back to Home" className={`-ml-2 mb-3 rounded-full p-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vexu ${isLight ? 'text-slate-500 hover:bg-white' : 'text-zinc-400 hover:bg-white/10'}`} data-id="element-1087" data-mp="38vr0gqo34qbo">
          <ArrowLeft className="h-5 w-5" data-id="element-1088" data-mp="2l6ogvd9w4nej" />
        </button>
        <h1 className={`text-2xl font-bold tracking-tight ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1089" data-mp="2q3584gcdzhvu">
          Meetings booked
        </h1>
        <p className={`mt-1 text-sm ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1090" data-mp="5dpl7wgagdbh">
          {AI_BOOKINGS.length} meetings Vexu booked from your calls
        </p>
      </header>

      <main className="flex-1 overflow-y-auto px-5 pb-10" data-id="element-1091" data-mp="aewrfr2s94g1">
        {groups.map((g, gi) => <section key={g.day} className={gi > 0 ? 'mt-6' : ''} aria-labelledby={`bookings-${g.day}`} data-id="element-1092" data-mp="26i1j97tt1mql" data-mpi={gi}>
            <h2 id={`bookings-${g.day}`} className={`mb-2 px-1 text-xs font-semibold ${g.day === TODAY_DAY ? isLight ? 'text-vexu-700' : 'text-vexu' : isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1093" data-mp="21iybhfmfx2ex">
              {dayLabel(g)}
            </h2>
            <ul className={`overflow-hidden rounded-2xl border ${isLight ? 'divide-y divide-slate-100 border-slate-200/80 bg-white' : 'divide-y divide-white/[0.06] border-white/[0.06] bg-[#141414]'}`} data-id="element-1094" data-mp="1npp57ntsgx0m">
              {g.items.map((e, i) => {
            const [start] = e.time.split(' – ');
            const TypeIcon = e.type === 'video' ? Video : Phone;
            return <motion.li key={e.id} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: (gi * 2 + i) * 0.04,
              duration: 0.22,
              ease: [0.23, 1, 0.32, 1]
            }} data-id="element-1095" data-mp="13z3p8jzt3mg5" data-mpi={i}>
                    <button type="button" onClick={() => onOpenInCalendar(e.id)} className={`flex w-full items-center gap-4 px-4 py-3.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-vexu ${isLight ? 'hover:bg-slate-50' : 'hover:bg-white/[0.03]'}`} data-id="element-1096" data-mp="uor4mwzvoga2">
                      <div className="w-16 flex-shrink-0" data-id="element-1097" data-mp="1dj638lia26xd">
                        <p className={`text-sm font-bold tabular-nums ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1098" data-mp="yngggl59pprl">{start}</p>
                        <p className={`text-[11px] ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1099" data-mp="13pfh3n9mi8ku">
                          {e.duration.replace(' minutes', ' min')}
                        </p>
                      </div>
                      <div className={`h-9 w-[3px] flex-shrink-0 rounded-full ${e.accent}`} aria-hidden data-id="element-1100" data-mp="8f25ajkq61mu" />
                      <div className="min-w-0 flex-1" data-id="element-1101" data-mp="3ovfigeh8b30">
                        <p className={`truncate text-sm font-semibold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-1102" data-mp="3naov1va9caf">{e.title}</p>
                        <p className={`mt-0.5 flex items-center gap-1.5 truncate text-xs ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-1103" data-mp="3pvtuhu0fw8st">
                          <TypeIcon className="h-3 w-3 flex-shrink-0" data-id="element-1104" data-mp="rd4w93wu28na" />
                          <span className="truncate" data-id="element-1105" data-mp="mejz5dcvo532">From call with {e.originatingCall?.caller ?? 'a caller'}</span>
                        </p>
                      </div>
                      <ChevronRight className={`h-4 w-4 flex-shrink-0 ${isLight ? 'text-slate-300' : 'text-zinc-600'}`} data-id="element-1106" data-mp="hhtj2q12p9hn" />
                    </button>
                  </motion.li>;
          })}
            </ul>
          </section>)}
        <p className={`mt-6 text-center text-xs ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-1107" data-mp="2x6d2uykn8vxn">
          Tap a meeting to see it in your calendar.
        </p>
      </main>
    </div>;
}